<div class="sm:max-w-xl mx-auto p-2 space-y-6">
  <!-- Payment Details Container -->
  <div class="border border-[#F1F1F1] p-2 px-3 rounded-[12px] gap-0 w-full">
    <!-- I Owe Section -->
    <section class="bg-[#f8f8f8] rounded-xl p-4 border border-[#F1F1F1] relative" id="received">
      <i
        class="ph ph-info no-arrow clickable tooltip-icon absolute right-2 top-2 z-10"
        matTooltip="{{ 'iOweExplanation' | translate }}"
        matTooltipPosition="below"
        toggleMatTooltip
      ></i>
      <!-- Header -->
      <header class="flex items-center gap-2 mb-2 md:mb-4 relative">
        <span
          class="payment-report-label text-left text-ellipsis confirmation-tab-mobile-label text-nowrap"
          [innerHTML]="getTranslatedString('iOwe', { receiver: '' })"
        ></span>
        @if (paymentStore.targetUserStateComputed()) {
          <app-nicky-user-guest-card
            [targetUser]="paymentStore.targetUserStateComputed()!"
            class="max-w-[72%]"
            [hideClearButton]="true"
            [noPayerInfoTooltip]="true"
          >
          </app-nicky-user-guest-card>
        }
      </header>

      <!-- Settlement Asset Selection -->
      <div class="flex flex-wrap items-center gap-4 w-full">
        <div class="flex flex-nowrap gap-2 w-full items-center">
          <!-- Asset Selector/Display -->
          @if (paymentStore.shouldShowAssetSelectorForDisplayComputed()) {
            <div class="asset-select-wrapper flex-centered w-[150px] md:w-[unset]">
              @if (paymentStore.settlementAssetOptionsForSelectionComputed().length > 0) {
                <app-crypto-select
                  [alreadySelectedCrypto]="paymentStore.settlementAssetTickerComputed()"
                  [cryptoOptions]="paymentStore.settlementAssetOptionsForSelectionComputed()"
                  (emitSelectEvent)="paymentStore.handleSettlementAssetSelection($event)"
                  inputId="settlement-asset-select"
                ></app-crypto-select>
              }
            </div>
          } @else {
            <div class="flex items-center gap-2">
              <mat-icon
                [svgIcon]="paymentStore.settlementAssetComputed()?.assetTicker || ''"
                [matTooltip]="paymentStore.settlementAssetComputed()?.assetTicker"
                toggleMatTooltip
                class="w-6 h-6"
              ></mat-icon>
              <span class="asset-label">{{ paymentStore.settlementAssetComputed()?.assetTicker }}</span>
            </div>
          }

          <!-- Amount Input/Display -->
          @if (paymentStore.shouldShowAmountInputForDisplayComputed()) {
            <div class="input-container">
              <input
                #input
                currencyMask
                [options]="getInputMask.getInputMask(paymentStore.settlementAssetTickerForInputComputed())"
                type="text"
                id="settlement-amount-input"
                inputmode="numeric"
                autocomplete="off"
                class="flex items-center gap-2 bg-white px-2 py-2 rounded-lg border border-gray-200 hover:border-gray-300 w-[90%] md:w-[190px] text-sm md:text-base placeholder:text-sm md:placeholder:text-base form-input-base"
                [ngModel]="paymentStore.settlementAmountForInputComputed()"
                (ngModelChange)="paymentStore.handleSettlementAmountChange($event)"
                (keydown.backspace)="paymentStore.handleAssetUpdate()"
                placeholder="{{
                  '0' +
                    (isCommaDecimal ? ',' : '.') +
                    paymentStore.generatePlaceholderZerosForAsset(paymentStore.payingAssetTickerForInputComputed())
                }}"
              />
            </div>
          } @else {
            <div class="w-full text-left">
              @if (paymentStore.isLoadingRatesForDisplayComputed()) {
                <div class="h-6 w-10 bg-gray-100 rounded-xl animate-pulse"></div>
              } @else {
                {{ paymentStore.formatPrice(paymentStore.settlementAmountForDisplayComputed(), true) }}
              }
            </div>
          }
        </div>

        <!-- Reference Input -->
        <div class="flex flex-nowrap gap-2 items-center md:w-[300px] truncate">
          <span class="text-left whitespace-nowrap text-sm sm:text-base italic confirmation-tab-mobile-label">
            {{ 'relativeTo' | translate }}
          </span>
          @if (paymentStore.shouldShowReferenceInputForDisplayComputed()) {
            <input
              placeholder="{{ 'payment_flow.reference' | translate }}"
              [ngModel]="paymentStore.referenceForInputComputed()"
              (ngModelChange)="paymentStore.handleReferenceChange($event)"
              [maxlength]="17"
              type="text"
              id="relative-to"
              class="flex placeholder:text-left items-center gap-2 bg-white px-2 py-2 rounded-lg border border-gray-200 hover:border-gray-300 w-[90%] md:w[unset] text-sm md:text-base placeholder:text-sm md:placeholder:text-base form-input-base [&&]:ring-0"
            />
          } @else {
            <span
              class="text-left truncate confirmation-tab-mobile-label"
              matTooltip="{{ paymentStore.referenceComputed() }}"
              matTooltipPosition="above"
              toggleMatTooltip
            >
              {{ paymentStore.referenceComputed() }}
            </span>
          }
        </div>
      </div>
    </section>

    <hr class="border-b-[0.5px] border-[#F1F1F1] my-[15px] w-[99%]" />

    <!-- I Am Sending Section -->
    <section class="bg-[#efffce] rounded-xl p-4 relative" id="paid">
      <i
        class="ph ph-info no-arrow clickable tooltip-icon absolute right-2 top-2"
        matTooltip="{{ 'iAmSendingExplanations' | translate }}"
        matTooltipPosition="above"
        toggleMatTooltip
      ></i>
      <!-- Header -->
      <header class="flex items-center gap-2 mb-2 md:mb-4">
        <span class="payment-report-label [&&]:italic confirmation-tab-mobile-label">{{
          'soIAmSending' | translate
        }}</span>
      </header>

      <!-- Payment Asset Selection -->
      <div class="flex items-center gap-3 flex-wrap">
        @if (paymentStore.hasSingleAssetOptionForDisplayComputed()) {
          <div class="flex items-center gap-2">
            <mat-icon [svgIcon]="paymentStore.payingAssetComputed()?.assetTicker || ''"></mat-icon>
            <span class="payment-report-label">{{ paymentStore.payingAssetComputed()?.assetTicker }}</span>
          </div>
        } @else {
          @if (paymentStore.assetOptionsForSelectionComputed().length > 0) {
            <app-crypto-select
              [inputId]="'payment-asset-select'"
              [cryptoOptions]="paymentStore.assetOptionsForSelectionComputed()"
              [alreadySelectedCrypto]="paymentStore.payingAssetTickerComputed()"
              (emitSelectEvent)="paymentStore.handlePayingAssetSelection($event)"
            ></app-crypto-select>
          }
        }

        <span class="text-base sm:text-xl confirmation-tab-mobile-label">
          @if (paymentStore.isLoadingRatesForDisplayComputed()) {
            <div class="h-6 w-24 bg-gray-100 opacity-30 rounded-xl animate-pulse"></div>
          } @else {
            {{ paymentStore.formatPrice(paymentStore.assetValueForDisplayComputed()) }}
          }
        </span>

        <!-- Network Selection -->
        @if (paymentStore.shouldShowNetworkSelectionForDisplayComputed()) {
          <div
            class="flex gap-2 w-full items-center flex-wrap md:flex-nowrap network-select-label confirmation-tab-mobile-label"
          >
            <span class="italic text-sm">{{ 'via' | translate }}</span>

            @for (enabledNetwork of paymentStore.enabledNetworksForSelectionComputed(); track enabledNetwork) {
              <button
                type="button"
                name="network"
                id="network"
                class="flex items-center gap-1 md:gap-3 p-2 rounded-md cursor-pointer border bg-white border-gray-300 flex-nowrap toggle-selection"
                (click)="changeNetwork(enabledNetwork)"
                [class]="paymentStore.networkForSelectionComputed() === enabledNetwork ? '[&&]:bg-[#deff96]' : ''"
              >
                <span class="text-xs leading-normal">
                  {{ paymentStore.getNetworkName(enabledNetwork) }}
                </span>
              </button>
            }
          </div>
        }
      </div>
    </section>
  </div>

  <!-- Current Rate Section -->
  @if (paymentStore.shouldShowRateSectionForDisplayComputed()) {
    <section class="space-y-2" id="rate">
      <!-- Rate Header -->
      <header class="flex items-center gap-2">
        <span class="payment-report-label confirmation-tab-mobile-label">{{
          'PAYMENT_REPORT.currentRate' | translate
        }}</span>
        <span class="rate-cooldown rates-asset-label w-10 md:w-11 confirmation-tab-mobile-label">
          {{
            !paymentStore.validateSameAssets() ? paymentStore.formatTimer(paymentStore.timerForDisplayComputed()) : ''
          }}
        </span>
        <i
          class="ph ph-info clickable tooltip-icon"
          matTooltip="{{ paymentStore.getCoinMarketTooltip() }}"
          matTooltipPosition="above"
          toggleMatTooltip
        ></i>
      </header>

      <!-- Rate Display -->
      <div class="text-gray-500 flex w-full gap-2 [&&]:!mt-0 items-center">
        @if (paymentStore.isLoadingRatesForDisplayComputed()) {
          <div class="flex items-center gap-2 w-full">
            <div class="h-6 w-10 bg-gray-100 opacity-30 rounded-xl animate-pulse"></div>
            <div class="h-6 w-10 bg-gray-100 opacity-30 rounded-xl animate-pulse"></div>
            <div class="h-6 w-10 bg-gray-100 opacity-30 rounded-xl animate-pulse"></div>
          </div>
        } @else {
          @if (!paymentStore.flippedRatesForToggleComputed()) {
            <span class="rates-asset-label text-base confirmation-tab-mobile-label">
              {{ paymentStore.payingAssetComputed()?.assetTicker }} 1 =
              {{ paymentStore.settlementAssetComputed()?.assetTicker }}
              {{ paymentStore.formatPrice(paymentStore.payingAssetPriceForDisplayComputed(), true) }}
            </span>
          } @else {
            <span class="rates-asset-label text-base confirmation-tab-mobile-label">
              {{ paymentStore.settlementAssetComputed()?.assetTicker }} 1 =
              {{ paymentStore.payingAssetComputed()?.assetTicker }}
              {{ paymentStore.formatPrice(1 / paymentStore.conversionRateForDisplayComputed()) }}
            </span>
          }
          <button
            class="ml-2 text-[#4f596b] font-medium rates-asset-label confirmation-tab-mobile-label"
            (click)="paymentStore.handleRateFlip()"
          >
            {{ 'flip' | translate }}
          </button>
        }
      </div>
    </section>
  } @else if (paymentStore.shouldShowRateErrorForDisplayComputed()) {
    <div class="text-red-500 my-2 text-left confirmation-tab-mobile-label">
      {{ 'PAYMENT_REPORT.weEncounteredATemporaryProblemFetchingTheRate' | translate }}
    </div>
  }

  @if (paymentStore.shouldShowNoWalletErrorForDisplayComputed()) {
    <div class="text-red-500 font-medium my-4 text-left confirmation-tab-mobile-label">
      {{
        'userTemporarilyUnableToReceivePayments'
          | translate: { assetName: paymentStore.payingAssetComputed()?.assetTicker }
      }}
    </div>
  }
</div>
